import Base from '../Base';
import Component from 'vue-class-component';
import { Button, TextInput, Spinner } from 'streamlabs-beaker';
import SubscriptionApi from '@/apis/subscription-api';
import braintree from 'braintree-web';
import paypal from 'paypal-checkout';
import NotificationHelper from '@/helpers/notification';
import { Prop } from 'vue-property-decorator';
import CheckoutPlans from './CheckoutPlans.vue';

type TPaymentMethod = 'paypal' | 'cc';
enum EPaymentMethod {
  paypal = 'paypal',
  cc = 'cc',
}

@Component({
  components: {
    SButton: Button,
    STextInput: TextInput,
    SSpinner: Spinner,
    CheckoutPlans,
  },
})
export default class PaymentForm extends Base {
  @Prop({ required: true })
  onSubmit: (any?) => {};

  braintreeToken: string = null;
  braintreeMode: 'production' | 'sandbox' = null;
  hostedFields: any = null;
  selectedPaymentMethod: TPaymentMethod = EPaymentMethod.cc;
  braintreePayload = null;
  paypalLoading: boolean = false;
  ccLoading: boolean = false;

  mounted() {
    this.initForm();
  }

  async initForm() {
    this.paypalLoading = true;
    this.ccLoading = true;

    const response = await SubscriptionApi.getToken();

    this.braintreeToken = response.token;
    this.braintreeMode = response.mode;

    this.loadPaymentForm();
  }

  get hostedFieldsStyles() {
    return {
      input: {
        'font-size': '14px',
        color: '#BDC2C4',
        'box-shadow': 'none',
      },
      ':focus, :active': {
        border: '2px solid #ffffff',
      },
      '.invalid': {
        color: '#EB5757',
      },
    };
  }

  get isLoading(): boolean {
    return this.paypalLoading || this.ccLoading;
  }

  get isCCSelected(): boolean {
    return this.selectedPaymentMethod === EPaymentMethod.cc;
  }

  get isPayPalSelected(): boolean {
    return this.selectedPaymentMethod === EPaymentMethod.paypal;
  }

  get paypalEmail(): string {
    return this.braintreePayload?.details?.email;
  }

  updatePaymentMethod(method: TPaymentMethod) {
    this.selectedPaymentMethod = method;
  }

  loadPaymentForm() {
    braintree.client.create({ authorization: this.braintreeToken }, (err, client) => {
      if (err) {
        console.log(err);
        return;
      }

      this.buildHostedFields(client);
      this.buildPayPal(client);
    });
  }

  buildHostedFields(client) {
    const fields = {
      cardholderName: {
        selector: '#cc-name',
        placeholder: 'John Dou',
      },
      number: {
        selector: '#card-number',
        placeholder: 'Card number',
      },
      cvv: {
        selector: '#cvv',
        placeholder: 'CVV',
      },
      expirationDate: {
        selector: '#expiration-date',
        placeholder: 'MM/YY',
      },
    };

    braintree.hostedFields.create(
      {
        client,
        fields,
        styles: this.hostedFieldsStyles,
      },
      (err, hostedFields) => {
        if (err) {
          console.log(err);
          return;
        }
        this.hostedFields = hostedFields;
        this.ccLoading = false;
      },
    );
  }

  buildPayPal(client) {
    braintree.paypalCheckout.create({ client }, (err, paypalCheckoutInstance) => {
      if (err) {
        NotificationHelper.error(err);
        return;
      }
      paypal.Button.render(
        {
          env: this.braintreeMode,
          style: { height: 51, size: 'responsive', shape: 'rect' },
          funding: { disallowed: [paypal.FUNDING.CREDIT] },
          payment: () => {
            return paypalCheckoutInstance.createPayment({ flow: 'vault' });
          },
          onAuthorize: (data) => {
            this.updatePaymentMethod(EPaymentMethod.paypal);
            return paypalCheckoutInstance.tokenizePayment(data, async (err, payload) => {
              this.braintreePayload = payload;
            });
          },
          onError: (err) => {
            console.log(err);
            return;
          },
        },
        '#paypal-button',
      ).then(() => {
        this.paypalLoading = false;
      });
    });
  }

  async submit() {
    if (this.selectedPaymentMethod === EPaymentMethod.paypal) {
      await this.onSubmit(this.braintreePayload);
    }

    this.hostedFields.tokenize(async (err, payload) => {
      if (err) {
        const invalidFields = err.details
          ? err.details.invalidFieldKeys
          : ['cardholderName', 'number', 'cvv', 'expirationDate'];
        invalidFields.forEach((field) => this.onHostedFieldsBlur(field, false));
        return;
      }
      this.braintreePayload = payload;
      await this.onSubmit(payload);
    });
  }

  onHostedFieldsBlur(field, isValid) {
    const fields = {
      cardholderName: 'cc-name',
      cvv: 'combined-card-field',
      expirationDate: 'combined-card-field',
      number: 'combined-card-field',
    };
    const errorClass = 'braintree-hosted-fields-invalid';
    const fieldElement = document.getElementById(fields[field]);
    !isValid ? fieldElement.classList.add(errorClass) : fieldElement.classList.remove(errorClass);
  }
}
